import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import {
  createDeal,
  createDealSection,
  deleteDeal,
  deleteDealSection,
  fetchDealSections,
  fetchDealStages,
  fetchDealPipeline,
  fetchDeals,
  fetchLatestDealNotes,
  renameDealSection,
  fetchAllDeals,
  fetchAllDealStages,
  fetchDealLastActivity,
  reorderDealSections,
  updateDeal,
} from './queries';
import type { DealUpdate } from './queries';
import type { Deal, Pipeline } from './types';
import { patchCachedRow, restoreCachedRows } from '../../lib/optimistic';

export const dealKeys = {
  all: ['deals'] as const,
  board: (pipeline: Pipeline) => [...dealKeys.all, 'board', pipeline] as const,
  sections: (pipeline: Pipeline) => [...dealKeys.all, 'sections', pipeline] as const,
  stages: (pipeline: Pipeline) => [...dealKeys.all, 'stages', pipeline] as const,
  notes: (pipeline: Pipeline) => [...dealKeys.all, 'notes', pipeline] as const,
};

function useActorId(): string {
  const { access } = useAuth();
  return access.status === 'approved' ? access.profile.id : '';
}

export function useDeals(pipeline: Pipeline) {
  const { client } = useAuth();
  return useQuery({
    queryKey: dealKeys.board(pipeline),
    queryFn: () => fetchDeals(client, pipeline),
    retry: false,
  });
}

/**
 * The pipeline a deep-linked deal belongs to, looked up only when the link
 * did not say. Disabled while `dealId` is null, so a normal visit to /deals
 * costs nothing. See fetchDealPipeline for the bug this answers.
 */
export function useDealPipeline(dealId: string | null) {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...dealKeys.all, 'pipeline-of', dealId],
    queryFn: () => (dealId === null ? Promise.resolve(null) : fetchDealPipeline(client, dealId)),
    enabled: dealId !== null,
    retry: false,
  });
}

export function useAllDeals() {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...dealKeys.all, 'every-pipeline'],
    queryFn: () => fetchAllDeals(client),
  });
}

export function useAllDealStages() {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...dealKeys.all, 'stages', 'every-pipeline'],
    queryFn: () => fetchAllDealStages(client),
  });
}

export function useDealLastActivity() {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...dealKeys.all, 'last-activity'],
    queryFn: () => fetchDealLastActivity(client),
  });
}

export function useDealSections(pipeline: Pipeline) {
  const { client } = useAuth();
  return useQuery({
    queryKey: dealKeys.sections(pipeline),
    queryFn: () => fetchDealSections(client, pipeline),
    retry: false,
  });
}

export function useDealStages(pipeline: Pipeline) {
  const { client } = useAuth();
  return useQuery({
    queryKey: dealKeys.stages(pipeline),
    queryFn: () => fetchDealStages(client, pipeline),
    retry: false,
  });
}

/** Depends on the deal list, so it waits for ids rather than guessing. */
export function useDealNotes(pipeline: Pipeline, dealIds: string[]) {
  const { client } = useAuth();
  return useQuery({
    queryKey: [...dealKeys.notes(pipeline), dealIds.length],
    queryFn: () => fetchLatestDealNotes(client, dealIds),
    enabled: dealIds.length > 0,
    retry: false,
  });
}

/** Everything a board write touches, refreshed together. */
function useBoardInvalidation(pipeline: Pipeline) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: dealKeys.all });
    void queryClient.invalidateQueries({ queryKey: dealKeys.board(pipeline) });
  };
}

export function useCreateDeal(pipeline: Pipeline) {
  const { client } = useAuth();
  const createdBy = useActorId();
  const refresh = useBoardInvalidation(pipeline);
  return useMutation({
    mutationFn: (input: {
      name: string;
      sectionId: string | null;
      position: number;
      leadId: string | null;
      companyId: string | null;
    }) => createDeal(client, { pipeline, ...input }, createdBy),
    onSuccess: refresh,
  });
}

/**
 * Every deal edit, including the drag between sections.
 *
 * Optimistic: a dragged card lands in its section immediately and goes back if
 * the write is refused. The same path carries the inline field edits, which
 * benefit for the same reason — a select that snaps back to its old value for
 * half a second reads as a bug rather than as latency.
 */
export function useUpdateDeal(pipeline: Pipeline) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const refresh = useBoardInvalidation(pipeline);
  return useMutation({
    mutationFn: (input: { dealId: string; changes: DealUpdate }) =>
      updateDeal(client, input.dealId, input.changes),
    onMutate: (input) =>
      patchCachedRow<Deal>(queryClient, dealKeys.board(pipeline), input.dealId, input.changes),
    onError: (_error, _input, context) => {
      restoreCachedRows(queryClient, dealKeys.board(pipeline), context);
    },
    onSettled: refresh,
  });
}

export function useDeleteDeal(pipeline: Pipeline) {
  const { client } = useAuth();
  const refresh = useBoardInvalidation(pipeline);
  return useMutation({
    mutationFn: (dealId: string) => deleteDeal(client, dealId),
    onSuccess: refresh,
  });
}

export function useCreateDealSection(pipeline: Pipeline) {
  const { client } = useAuth();
  const refresh = useBoardInvalidation(pipeline);
  return useMutation({
    mutationFn: (input: { name: string; position: number }) =>
      createDealSection(client, { pipeline, ...input }),
    onSuccess: refresh,
  });
}

export function useReorderDealSections(pipeline: Pipeline) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (updates: { id: string; position: number }[]) =>
      reorderDealSections(client, updates),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dealKeys.sections(pipeline) });
    },
  });
}

export function useRenameDealSection(pipeline: Pipeline) {
  const { client } = useAuth();
  const refresh = useBoardInvalidation(pipeline);
  return useMutation({
    mutationFn: (input: { sectionId: string; name: string }) =>
      renameDealSection(client, input.sectionId, input.name),
    onSuccess: refresh,
  });
}

export function useDeleteDealSection(pipeline: Pipeline) {
  const { client } = useAuth();
  const refresh = useBoardInvalidation(pipeline);
  return useMutation({
    mutationFn: (sectionId: string) => deleteDealSection(client, sectionId),
    onSuccess: refresh,
  });
}
